import { TableRow, TableRowCell } from './styled';
import { Grid, Box, Text, useTheme } from '@simplywallst/ui-core';
import { SymbolCell } from './SymbolCell';
import { categorizeScore, getFormattedValues } from './utils';
import type { DividendHoldingItem } from '../../../types/portfolioDividend';
import StatementPositiveSolid from '@simplywallst/ui-core/icons/StatementPositiveSolid';
import StatementNegativeSolid from '@simplywallst/ui-core/icons/StatementNegativeSolid';
import StatementWarningSolid from '@simplywallst/ui-core/icons/StatementWarningSolid';
import LockSolid from '@simplywallst/ui-core/icons/LockSolid';
import { useMemo } from 'react';

interface Props {
  holding: DividendHoldingItem;
  isSticky?: boolean;
  isAnalysisRestricted?: boolean;
  currencyISO: string;
}

export const HoldingRow = ({
  holding,
  isSticky,
  isAnalysisRestricted,
  currencyISO,
}: Props) => {
  const theme = useTheme();

  const scoreCategory = categorizeScore(holding.dividendScore || -1);

  const company = {
    tickerSymbol: holding.tickerSymbol,
    name: holding.name,
    canonicalURL: holding.canonicalURL,
    score: holding.score,
  };

  const scoreIcon = useMemo(() => {
    switch (scoreCategory) {
      case 'good':
        return (
          <StatementPositiveSolid
            data-cy-id="icon-score-good"
            width={16}
            height={16}
            fill={theme.color.good}
          />
        );
      case 'bad':
        return (
          <StatementNegativeSolid
            data-cy-id="icon-score-bad"
            width={16}
            height={16}
            fill={theme.color.bad}
          />
        );
      default:
        return (
          <StatementWarningSolid
            data-cy-id="icon-score-bad"
            width={16}
            height={16}
            fill={theme.color.warning}
          />
        );
    }
  }, [scoreCategory, theme.color.bad, theme.color.good, theme.color.warning]);

  const formattedValues = getFormattedValues(holding, currencyISO);
  const isRestricted = isAnalysisRestricted || holding.isAnalysisRestricted;
  const lockFillColor = theme.color.chart05RGBA(0.3);

  return (
    <TableRow tabIndex={0}>
      <SymbolCell company={company} isSticky={isSticky} />

      <TableRowCell>
        {isRestricted ? (
          <LockSolid aria-label="Blocked content" fill={lockFillColor} />
        ) : (
          <Box>
            <div>
              <Text as="span" typography="xSmall" color="textSolid">
                {formattedValues.payment}
              </Text>
              <Text as="span" typography="xSmall" color="textSofter">
                / year
              </Text>
            </div>
            <Text typography="tiny" color="textSofter" lineClamp={1}>
              {formattedValues.contribution}
            </Text>
          </Box>
        )}
      </TableRowCell>

      <TableRowCell>
        {isRestricted ? (
          <LockSolid aria-label="Blocked content" fill={lockFillColor} />
        ) : (
          <Box>
            <Text typography="xSmall" color="textSolid" lineClamp={1}>
              {formattedValues.dividendYield}
            </Text>
            <Text typography="tiny" color="textSofter" lineClamp={1}>
              {formattedValues.yieldOnCost}
            </Text>
          </Box>
        )}
      </TableRowCell>

      <TableRowCell>
        {isRestricted ? (
          <LockSolid aria-label="Blocked content" fill={lockFillColor} />
        ) : (
          <Box>
            <Grid
              templateColumns="max-content max-content"
              alignItems="center"
              gap="4px"
            >
              {scoreIcon}
              <Text typography="xSmall" color={scoreCategory}>
                {formattedValues.dividendScore}/6
              </Text>
            </Grid>

            <Text
              typography="tiny"
              color="whiteRGBA"
              opacity={0.5}
              lineClamp={1}
            >
              {formattedValues.dividendGrowth}
            </Text>
          </Box>
        )}
      </TableRowCell>
    </TableRow>
  );
};
